import { AxiosResponse } from "axios";
import { GeoJsonInformativeProperties } from "models/informatives.model";
import { PagedResponse } from "models/paged-response";
import { all, call, put, takeLatest } from "redux-saga/effects";
import { createPostFailed, createPostRequest, createPostSuccess } from "store/modules/posts/actions/createPost";
import { deletePostFailed, deletePostRequest, deletePostSuccess } from "store/modules/posts/actions/deletePost";
import { getEssentialServicesGeojsonFailed, getEssentialServicesGeojsonRequest, getEssentialServicesGeojsonSuccess } from "store/modules/posts/actions/getEssentialServicesGeojson";
import { getInformativesGeojsonFailed, getInformativesGeojsonRequest, getInformativesGeojsonSuccess } from "store/modules/posts/actions/getInformativesGeojson";
import { getPostByIdFailed, getPostByIdRequest, getPostByIdSuccess } from "store/modules/posts/actions/getPostById";
import { getPostsPaginatedFailed, getPostsPaginatedRequest, getPostsPaginatedSuccess } from "store/modules/posts/actions/getPostsPaginated";
import { publishPostFailed, publishPostRequest, publishPostSuccess } from "store/modules/posts/actions/publishPost";
import { updatePostFailed, updatePostRequest, updatePostSuccess } from "store/modules/posts/actions/updatePost";
import { CreatePostRequest, DeletePostRequest, GetPostsPaginatedRequest, PostByIdRequest, PublishPostRequest, UpdatePostRequest } from "store/modules/posts/types";
import api from "_config/api";

function* getPostsPaginated(params: {
    type: string;
    payload: GetPostsPaginatedRequest;
}) {
    try {
        const route = params.payload.postType === "INFORMATIVO" ? "/informativos" : "/pontosEssenciais";
        const response: AxiosResponse<PagedResponse<any>> = yield call(
            api.get,
            `${route}/paginated`,
            {
                params: {
                    pageIndex: params.payload.index,
                    pageSize: params.payload.pageSize,
                    search: params.payload.search,
                    dataInicio: params.payload.startDate ? params.payload.startDate : undefined,
                    dataFim: params.payload.endDate ? params.payload.endDate : undefined,
                    publicado: params.payload.onlyPublished ?
                        true : undefined
                }
            }
        );

        yield put(getPostsPaginatedSuccess({
            ...response.data,
            items: response.data.items.map(x => ({
                ...x,
                id: x.idInformativo ?? x.idPontoEssencial
            }))
        }));
    } catch (error) {
        yield put(getPostsPaginatedFailed([]));
    }
}

function* createPost(params: {
    type: string;
    payload: CreatePostRequest;
}) {
    try {
        const route = params.payload.postType === "INFORMATIVO" ? "/informativos" : "/pontosEssenciais";        
        const response: AxiosResponse = yield call(
            api.post,
            `${route}`,
            { ...params.payload.post, imagem: null }
        );

        if (params.payload.post.imagem) {
            const formData = new FormData();
            formData.append('Anexo', params.payload.post.imagem);
            yield call(
                api.post,
                `${route}/arquivo/${response.data.idInformativo ?? response.data.idPontoEssencial}`,
                formData
            );
        }

        yield put(createPostSuccess());
        params.payload.onSuccess?.();
    } catch (error) {
        yield put(createPostFailed([]));
        params.payload.onFailed?.();
    }
}

function* updatePost(params: {
    type: string;
    payload: UpdatePostRequest;
}) {
    try {
        const route = params.payload.postType === "INFORMATIVO" ? "/informativos" : "/pontosEssenciais";
        const response: AxiosResponse = yield call(
            api.put,
            `${route}/${params.payload.postId}`,
            { ...params.payload.post, imagem: null }
        );
        if (params.payload.post.imagem) {
            const formData = new FormData();
            formData.append('Anexo', params.payload.post.imagem);
            yield call(
                api.post,
                `${route}/arquivo/${params.payload.postId}`,
                formData
            )
        }
        yield put(updatePostSuccess());
        params.payload.onSuccess?.();
    } catch (error) {
        yield put(updatePostFailed([]));
        params.payload.onFailed?.();
    }
}

function* deletePost(params: {
    type: string;
    payload: DeletePostRequest;
}) {
    try {
        const route = params.payload.postType === "INFORMATIVO" ? "/informativos" : "/pontosEssenciais";
        const idKey = params.payload.postType === "INFORMATIVO" ? "idInformativo" : "idPontoEssencial";
        const response: AxiosResponse = yield call(
            api.delete,
            `${route}`,
            { data: { [idKey]: params.payload.postId } }
        );

        yield put(deletePostSuccess(null));
        params.payload.onSuccess?.();
    } catch (error) {
        yield put(deletePostFailed([]));
        params.payload.onFailed?.();
    }
}

function* getPostById(params: {
    type: string;
    payload: PostByIdRequest;
}) {
    try {
        const route = params.payload.postType === "INFORMATIVO" ? "/informativos" : "/pontosEssenciais";
        const response: AxiosResponse<any> = yield call(
            api.get,
            `${route}/${params.payload.postId}`
        );
        const item = response.data;
        if (item) {
            yield put(getPostByIdSuccess({
                ...item,
                photoAsFile: `${process.env.REACT_APP_API}${route}/imagem/${params.payload.postId}`
            }));
        } else {
            yield put(getPostByIdSuccess(null));
        }
    } catch (error) {
        yield put(getPostByIdFailed([]));
    }
}

function* getInformativesGeojson(params: {
    type: string;
}) {
    try {
        const response: AxiosResponse<GeoJSON.FeatureCollection<GeoJSON.Point, GeoJsonInformativeProperties>> = yield call(
            api.get,
            "/informativos/geojson"
        );

        yield put(getInformativesGeojsonSuccess(response.data));
    } catch (error) {
        yield put(getInformativesGeojsonFailed([]));
    }
}

function* getEssentialServicesGeojson(params: {
    type: string;
}) {
    try {
        const response: AxiosResponse<GeoJSON.FeatureCollection<GeoJSON.Point, GeoJsonInformativeProperties>> = yield call(
            api.get,
            "/pontosEssenciais/geojson"
        );

        yield put(getEssentialServicesGeojsonSuccess(response.data));
    } catch (error) {
        yield put(getEssentialServicesGeojsonFailed([]));
    }
}

function* publishPost(params: {
    type: string;
    payload: PublishPostRequest;
}) {
    try {
        const route = params.payload.postType === "INFORMATIVO" ? "/informativos" : "/pontosEssenciais";
        yield call(
            api.put,
            `${route}/${params.payload.postId}/${params.payload.willPublish ? 'publicar' : 'despublicar'}`
        );

        yield put(publishPostSuccess());
        params.payload.onSuccess?.();
    } catch (error) {
        yield put(publishPostFailed([]));
        params.payload.onFailed?.();
    }
}

async function createFile(url: string) {
    // const response = await api.get(`${url}`, {
    //     responseType: 'blob',    
    //     headers: {
    //         'Access-Control-Allow-Origin': '*'
    //     }        
    // });
    // // const data = await response.blob();
    // return await new Promise((resolve) => {
    //     const metadata = {
    //         type: 'application/octet-stream',
    //     };
    //     let reader = new FileReader()
    //     reader.addEventListener('loadend', () => {
    //         let contents = reader.result
    //         resolve(contents);
    //     })
    //     reader.readAsDataURL(response.data);
    // });
    // const file = new File([data], "test.png", metadata);
    // return file;
}


export default all([
    takeLatest(getPostsPaginatedRequest.type, getPostsPaginated),
    takeLatest(createPostRequest.type, createPost),
    takeLatest(updatePostRequest.type, updatePost),
    takeLatest(deletePostRequest.type, deletePost),
    takeLatest(getPostByIdRequest.type, getPostById),
    takeLatest(getInformativesGeojsonRequest.type, getInformativesGeojson),
    takeLatest(getEssentialServicesGeojsonRequest.type, getEssentialServicesGeojson),
    takeLatest(publishPostRequest.type, publishPost),
]);
